// delivery-report.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';

/** ========= Shared enums (copy from your delivery.service if needed) ========= */
export type LoadState = 'draft' | 'in_transit' | 'ready_settlement' | 'settled';

/** ========= Request ========= */
export interface LoadReportRequest {
  load_id: number;
}

/** ========= Models (as produced by /v1/api/delivery/report) ========= */
export interface LoadReportLoad {
  id: number;
  name: string;
  state: LoadState;
  deliveryBoyId: number | null;
  deliveryBoyName: string | null;
  salesManId: number | null;
  salesManName: string | null;
  createdAt: string | null;  // 'YYYY-MM-DD HH:mm:ss'
  updatedAt: string | null;  // 'YYYY-MM-DD HH:mm:ss'
  note?: string; 
}

export interface LoadReportRow {
  slno: number;                              // sequence number per (SO, Invoice)
  customerName: string | null;
  orderNumber: string | null;                // Sales Order number
  amount: number;                            // Sales Order total
  invoiceNumber: string | null;
  invoiceAmount: number;
  oldUnpaidInvoiceNumbers: string[];         // excludes current invoice
  oldUnpaidTotalAmount: number;              // sum of residual on those invoices
  total: number;                             // invoiceAmount + oldUnpaidTotalAmount
  notes: string;                             // from a1.delivery.invoice link (if any)
}

export interface LoadReportTotals {
  invoiceAmount: number;
  oldUnpaidTotalAmount: number;
  total: number;
}

/** ========= Response =========
 * NOTE: Our BaseApiService typically unwraps `data` from { status, data }.
 * So we model the unwrapped shape directly, like other responses in your app.
 */
export interface LoadReportResponse {
  status: 'success' | 'error';
  message?: string;
  load?: LoadReportLoad;
  rows?: LoadReportRow[];
  count?: number;
  totals?: LoadReportTotals;
}

/** ========= Service ========= */
@Injectable({ providedIn: 'root' })
export class DeliveryReportService extends BaseApiService {
  constructor(http: HttpClient, busy: BusyService) {
    super(http, busy);
  }

  /** POST /v1/api/delivery/report
   * Returns one row per (SO, Invoice) on the load with old unpaid breakdown.
   */
  getLoadReport(loadId: number): Observable<LoadReportResponse> {
    const body: LoadReportRequest = { load_id: loadId };
    return this.post<LoadReportResponse>('delivery/report', body);
  }

  /** Convenience: returns just the rows (or throws on error) */
  getLoadReportRows(loadId: number): Observable<LoadReportRow[]> {
    return this.getLoadReport(loadId).pipe(
      map(res => {
        if (res.status === 'success' && res.rows) return res.rows;
        throw new Error(res.message || 'Failed to load report');
      })
    );
  }
}
